import { errorCodes } from '@/services/api/errorCodes';
import {
  checkTokenPrice,
  checkTrxPrice,
  fetchBalance,
  fetchFee,
  fetchTokenTransactions,
  fetchTransactions,
  sendAsset,
  sendTrx,
  verifyAddress,
} from '@/services/api/HomeApi';
import { getStores } from '@/stores';
import { toBalance, toTransactions } from '../transfer/toActivities';
import { IRequestSendAsset } from '@/core/domain/entities/request';
import { toMyAsset } from '../transfer/toUser';
import { DUPLICATE_SEND_TIME } from '@/consts';

/**
 * 트랜잭션 조회
 * @param {string} address
 * @param {number} count
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryFetchTransactions(
  address: string,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const res = await fetchTransactions({ address });

    // 에러 코드(문자열)인 경우
    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    // 정상적인 트랜잭션 데이터인 경우
    presentationStore.setTransactions(toTransactions(res));

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * 토큰 트랜잭션 조회
 * @param {string} address
 * @param {number} count
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryFetchTokenTransactions(
  address: string,
  count?: number,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const res = await fetchTokenTransactions({ address, count });

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setTokenTransactions(toTransactions(res));

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function tryCheckTokenPrice(): Promise<{
  succed: boolean;
  errorProcceded?: boolean;
  errorMessage?: string;
}> {
  try {
    const { presentationStore } = getStores();
    const res = await checkTokenPrice();

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setTokenPrice(res);

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function tryCheckTrxPrice(): Promise<{
  succed: boolean;
  errorProcceded?: boolean;
  errorMessage?: string;
}> {
  try {
    const { presentationStore } = getStores();
    const res = await checkTrxPrice();

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setTrxPrice(res);

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function trySendAsset(
  reqData: IRequestSendAsset,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const currentTransferData = presentationStore.transferData;

    // 이전 전송 데이터와 현재 요청을 비교 (먼저 체크)
    if (
      currentTransferData &&
      currentTransferData.from === reqData.from &&
      currentTransferData.amount === reqData.amount &&
      new Date().getTime() - new Date(currentTransferData.sendTime).getTime() < DUPLICATE_SEND_TIME
    ) {
      return { succed: false };
    }

    // 전송 시도
    const res = await sendAsset(reqData);

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    // 전송 성공 시 transferData 설정 (성공한 전송 기록)
    presentationStore.setTransferData({
      from: reqData.from,
      amount: reqData.amount,
      sendTime: new Date().toISOString(),
    });

    return { succed: true };
  } catch (error) {
    console.error(error);
    return { succed: false };
  }
}

export async function trySendTrx(
  reqData: IRequestSendAsset,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const currentTransferData = presentationStore.transferData;

    // 이전 전송 데이터와 현재 요청을 비교 (먼저 체크)
    if (
      currentTransferData &&
      currentTransferData.from === reqData.from &&
      currentTransferData.amount === reqData.amount &&
      new Date().getTime() - new Date(currentTransferData.sendTime).getTime() < DUPLICATE_SEND_TIME
    ) {
      return { succed: false };
    }

    const res = await sendTrx(reqData);

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    // 전송 성공 시 transferData 설정 (성공한 전송 기록)
    presentationStore.setTransferData({
      from: reqData.from,
      amount: reqData.amount,
      sendTime: new Date().toISOString(),
    });

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function tryVerifyAddress(
  address: string,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const res = await verifyAddress(address);
    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function tryFetchBalance(
  address: string,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore, userStore } = getStores();

    const res = await fetchBalance(address);

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setBalance(toBalance(res));
    userStore.setMyAsset(toMyAsset(res));

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function tryFetchFee(
  coin: string,
  address: string,
  amount: number,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const res = await fetchFee({ coin, address, amount });
    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setFee(res);

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}
